/**
 * Artifact context panel (CONTRACT_V2 §3.4 / D3 advisory invariants).
 *
 * Shows the contexts resolved for an artifact — its direct `primary_context_id`
 * plus `context_bindings` of `entity_type='artifact'` — each with a relevance
 * score, fetched from `GET /api/artifacts/:id/contexts` (`{ items: [...] }`).
 *
 * Per D3 the panel is purely advisory: it NEVER blocks anything. When the
 * artifact has no contexts (or only low-maturity ones) it surfaces a soft
 * "needs richer context" nudge instead of an error. The fetch resolves to an
 * empty list on any failure so the artifact show page always renders.
 *
 * Fetch pattern mirrors `pages/Dashboard.tsx`: `useApiUrl()` + native fetch in
 * an effect (the portal's canonical custom-GET approach).
 */
import { useEffect, useState } from "react";
import { useApiUrl, useGo, useNavigation } from "@refinedev/core";
import { Info } from "@/components/ui/icon";
import {
  CONTEXT_MATURITY_STAGES,
  type Context,
  type ContextMaturityStage,
} from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadingState } from "@/components/ui/states";

/**
 * A context resolved for an artifact with its relevance score. Mirrors the
 * worker's `ScoredContext` view returned by `GET /api/artifacts/:id/contexts`
 * (not a shared type — the score field name is read defensively).
 */
type ScoredContext = Context & {
  relevance_score?: number | null;
  score?: number | null;
};

interface ArtifactContextsResponse {
  items: ScoredContext[];
}

/** Maturity stages considered "rich" enough that the nudge is suppressed. */
const LOW_MATURITY: ReadonlySet<ContextMaturityStage> = new Set([
  "ambient_field",
  "attention_field",
  "signal_field",
]);

function relevanceOf(ctx: ScoredContext): number | null {
  if (typeof ctx.relevance_score === "number") return ctx.relevance_score;
  if (typeof ctx.score === "number") return ctx.score;
  return null;
}

function humanizeStage(stage: string | null | undefined): string {
  if (!stage) return "—";
  return stage
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Soft, never-blocking advisory callout. Neutral muted tone (BLACK-only accent,
 * design-system §1) — advisory, not alarming.
 */
function ContextNudge({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <p className="text-muted-foreground">{message}</p>
    </div>
  );
}

export function ContextPanel({ artifactId }: { artifactId: string }) {
  const apiUrl = useApiUrl();
  const go = useGo();
  const { showUrl, editUrl } = useNavigation();
  const [items, setItems] = useState<ScoredContext[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetch(`${apiUrl}/artifacts/${artifactId}/contexts`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        return (await res.json()) as ArtifactContextsResponse;
      })
      .then((data) => {
        setItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        // D3: advisory only — never surface as a page error.
        if (!controller.signal.aborted) setItems([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [apiUrl, artifactId]);

  // Order known maturity stages by spec progression so the highest sorts first.
  const stageRank = (stage: string): number => {
    const idx = CONTEXT_MATURITY_STAGES.indexOf(stage as ContextMaturityStage);
    return idx === -1 ? -1 : idx;
  };

  const hasContexts = items.length > 0;
  const allLowMaturity =
    hasContexts &&
    items.every((c) => LOW_MATURITY.has(c.maturity_stage));

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Resolved operating contexts for this artifact (advisory).
      </p>
      {loading ? (
        <LoadingState label="Resolving contexts…" rows={3} />
      ) : !hasContexts ? (
          <ContextNudge message="This artifact is not linked to any context yet. Linking it to an operating context will improve how the system resolves rules, evidence, and packages around it." />
        ) : (
          <>
            <ul className="space-y-2">
              {items
                .slice()
                .sort(
                  (a, b) =>
                    stageRank(b.maturity_stage) - stageRank(a.maturity_stage),
                )
                .map((ctx) => {
                  const rel = relevanceOf(ctx);
                  return (
                    <li
                      key={ctx.id}
                      className="flex items-center justify-between gap-2 rounded-md border border-border p-2"
                    >
                      <button
                        type="button"
                        className="min-w-0 text-left"
                        onClick={() =>
                          go({ to: showUrl("contexts", ctx.id) })
                        }
                      >
                        <span className="block truncate text-sm font-medium hover:underline">
                          {ctx.name}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {humanizeStage(ctx.context_type)} ·{" "}
                          {humanizeStage(ctx.maturity_stage)}
                        </span>
                      </button>
                      <Badge variant="secondary" className="shrink-0">
                        {rel === null ? "—" : rel.toFixed(2)}
                      </Badge>
                    </li>
                  );
                })}
            </ul>
            {allLowMaturity ? (
              <ContextNudge message="The linked contexts are still early-stage (ambient / attention / signal field). Maturing or adding a more structured context would give this artifact richer operating context." />
            ) : null}
          </>
        )}
      <Button
        variant="outline"
        size="sm"
        onClick={() => go({ to: editUrl("artifacts", artifactId) })}
      >
        Edit context links
      </Button>
    </div>
  );
}

export default ContextPanel;
